import {
  UsersIcon,
  CheckCircleIcon,
  MapIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Popup, TWclassNames } from "./utils/Div";
import { Link } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { PMNotice } from "./utils/notifications";
import SimplePrompt from "./utils/simplePrompt";
import CustomerDialog from "./dashboard/customers/dialog";
import parse from "html-react-parser";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { status } from "./utils/statusMap";
import {
  promangToast,
} from "../operationFeedback";

export {
  dispatchWithToast,
  dispatchWithToastV2,
  dispatchWithToastPromise,
  axiosWithToast,
} from "../operationFeedback/legacyToastAdapters";

/** Clipboard and misc helpers use the shared queue (not raw hot-toast). */
const toast = {
  success: (message, options) => promangToast.success(message, options),
  error: (message, options) => promangToast.error(message, options),
};

export const mapErrors = (errors) => {
  const mappedErrors = [];
  if (!errors) return false;
  for (const [, value] of Object.entries(errors)) {
    mappedErrors.push(value.message);
  }
  return mappedErrors.join(", ");
};

export const formatter = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
});

/** Safely convert any value to string for display. Prevents "Objects are not valid as React child" errors. */
export function safeString(val, fallback = "") {
  if (val == null) return fallback;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  return fallback;
}

export const dateToString = (dateTime) => {
  if (dateTime == null) return null;
  if (typeof dateTime !== "string" && typeof dateTime !== "number") return null;
  const str = typeof dateTime === "string" ? dateTime : String(dateTime);

  const locale = "nb-NO";
  const hasTime = str?.split("T")[1] ? true : false;

  let options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };

  if (hasTime) {
    options = {
      ...options,
      time: true,
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    };
  }

  const date = new Date(str).toLocaleString(locale, options);

  return date;
};

export const STATUSES = [
  { id: 1, title: "New Case", value: "NEW" },
  { id: 2, title: "Being procesed", value: "PROCESING" },
  { id: 3, title: "Finished", value: "FINISHED" },
];

export { status };

export const mapsServiceUrl = (address, preferred_maps_service) => {
  if (preferred_maps_service === "GOOGLE_MAPS") {
    return "https://www.google.com/maps?q=" + address.replace(/ /g, "+");
  } else if (preferred_maps_service === "APPLE_MAPS") {
    return "https://maps.apple.com/?q=" + address.replace(/ /g, "+");
  } else {
    return "https://www.google.com/maps?q=" + address.replace(/ /g, "+");
  }
};
export const mapsServiceIcon = (preferred_maps_service) => {
  if (preferred_maps_service === "GOOGLE_MAPS") {
    return <MapIcon className="h-4 w-4" aria-hidden="true" />;
  } else if (preferred_maps_service === "APPLE_MAPS") {
    return <MapIcon className="h-4 w-4" aria-hidden="true" />;
  } else {
    return <MapIcon className="h-4 w-4" aria-hidden="true" />;
  }
};

// Update the URL parameters
// Updates all params in the url, except the "exception" param
export const updateUrlWithException = (newParams, exception) => {
  // Update the URL parameters
  const searchParams = new URLSearchParams(window.location.search);
  Object.entries(newParams).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
  });
  // Preserve the existing filters parameter
  const filtersParam = searchParams.get(exception);
  if (filtersParam) {
    searchParams.set(exception, filtersParam);
  }

  // Construct the updated URL with all the search parameters
  const queryParams = Array.from(searchParams)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  const newUrl = `${window.location.pathname}?${queryParams}`;
  window.history.pushState({}, "", newUrl);
};

export const updateUrl = (selectedFilters) => {
  // Construct the filter values string from the selected filters
  const filterValues = selectedFilters
    ?.map((filter) => `${filter.name}:${filter?.values?.join("|")}`)
    .join(",");

  // Update the URL parameters
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("filters", filterValues);

  // Construct the updated URL with all the search params
  const queryParams = Array.from(searchParams)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  const newUrl = `${window.location.pathname}?${queryParams}`;
  window.history.pushState({}, "", newUrl);
};

export function StringLimit({ text, limit }) {
  // Guard: never render objects - prevents "Objects are not valid as React child"
  const str =
    text == null
      ? ""
      : typeof text === "string" || typeof text === "number"
        ? String(text)
        : "";
  if (!str) return null;
  if (str.length <= limit) {
    return <span>{str}</span>;
  }
  return (
    <Popup content={str} textSize="md">
      <span>{str.slice(0, limit)}...</span>
    </Popup>
  );
}

// Standard headerActions for DashboardHeader

export const customerHeaderAction = (customer) => {
  return {
    label: customer?.displayTitle,
    description: "",
    path: "/dashboard/customers/" + customer?.id,
    action: null,
    icon: UsersIcon,
    textColor: "text-teal-700",
    hidden: customer?.id ? false : true,
  };
};

export function last8Digits(number) {
  return number.toString().slice(-8);
}

export function ItemStatus({ item }) {
  const { t } = useTranslation();
  const statusVal = item?.status;

  if (!status[statusVal] || typeof statusVal === "object") {
    return typeof statusVal === "string" || typeof statusVal === "number" ? statusVal : null;
  }

  return (
    <span
      className={TWclassNames(
        statusVal ? status[statusVal]?.class : "",
        "inline-flex rounded-full px-2 text-xs font-semibold leading-5"
      )}
    >
      {statusVal ? <span> {t(status[statusVal]?.label)} </span> : "None"}
    </span>
  );
}

export function SyncStatus({ item }) {
  return (
    <span>
      <CheckCircleIcon
        className={TWclassNames(
          item?.syncStatus ? "text-green-400" : "text-red-400",
          "w-5 h-5"
        )}
      />
    </span>
  );
}
export default function UppercaseFirstLetter({ text }) {
  const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1);

  return <div>{capitalizedText}</div>;
}

export const upperFirstLetter = (text) => {
  const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1);
  return capitalizedText;
};

export function pageOptions({ perPage, totalCount }) {
  const itemsPerPage = perPage > 1 ? perPage : 200;

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const pageOptions = [];

  for (let i = 1; i <= totalPages; i++) {
    pageOptions.push(i);
  }

  return pageOptions;
}

export function formattedDate(date) {
  return new Date(date).toLocaleString();
}

export const productPath = ({ product }) => {
  return "/dashboard/products/" + product?.id;
};

export const reportPath = ({ report }) =>
  "/dashboard/service/reports/" + report?.id;

export const orderPath = ({ order }) => {
  return "/dashboard/orders/" + order?.id;
};

export const casePath = ({ caseItem }) => {
  return "/dashboard/cases/" + caseItem.id;
};

export const customerPath = ({ customer }) =>
  "/dashboard/customers/" + customer?.id;

export const userPath = ({ user }) => "/dashboard/users/" + user?.id;

export const projectPath = ({ project }) =>
  "/dashboard/projects/" + project?.id;

export const workOrderPath = ({ workOrder }) =>
  "/dashboard/work-orders/" + workOrder?.id;

export const activityPath = ({ activity }) =>
  "/dashboard/activities/" + activity?.id;

export const employeePath = ({ employee }) =>
  "/dashboard/employees/" + employee?.id;

export const serviceAgreementPath = ({ serviceAgreement }) =>
  "/dashboard/service/agreements/" + serviceAgreement?.id;

export const deliveryAddressGooglePath = ({ deliveryAddress }) =>
  "https://www.google.com/maps?q=" +
  deliveryAddress?.address +
  ", " +
  deliveryAddress?.postal +
  " " +
  deliveryAddress?.place;

export const ProjectTitle = ({
  project,
  path = projectPath({ project: project }),
  className,
  folder,
  ellipsis = false,
}) => {
  const text = folder?.id
    ? `${project?.displayTitle ?? ""} / ${folder?.displayTitle ?? ""}`.trim()
    : project?.displayTitle;
  return (
    <Link
      to={path}
      className={ellipsis ? "block min-w-0 max-w-full" : undefined}
      title={ellipsis && text ? text : undefined}
    >
      <div
        className={TWclassNames(
          className ? className : "text-blue-400",
          ellipsis && "truncate"
        )}
      >
        {project?.displayTitle}{" "}
        {folder?.id && " / " + folder?.displayTitle}
      </div>
    </Link>
  );
};

export const WorkOrderTitle = ({
  workOrder,
  path = workOrderPath({ workOrder: workOrder }),
  className,
}) => {
  return (
    <Link to={path}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {workOrder?.displayTitle}
      </div>
    </Link>
  );
};

export const ActivityTitle = ({
  activity,
  path = activityPath({ activity: activity }),
  className,
}) => {
  return (
    <Link to={path}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {activity?.displayTitle}
      </div>
    </Link>
  );
};

export const EmployeeTitle = ({
  employee,
  // path = employeePath({ employee: employee }),
  path = null,
  className,
  folder: _folder,
}) => {
  const Content = () => (
    <div
      className={TWclassNames(
        className ? className : path ? "text-blue-400" : ""
      )}
    >
      {employee?.displayTitle}
    </div>
  );
  if (path) {
    return (
      <Link to={path}>
        <Content />
      </Link>
    );
  } else {
    return (
      <span>
        <Content />
      </span>
    );
  }
};

export const ServiceAgreementTitle = ({
  serviceAgreement,
  target = "_blank",
  path = serviceAgreementPath({
    serviceAgreement: serviceAgreement,
  }),
  className,
  /** When true, link is block-level with ellipsis and native tooltip for full title (e.g. data tables). */
  ellipsis = false,
}) => {
  const text = serviceAgreement?.displayTitle;
  return (
    <Link
      to={path}
      target={target}
      className={ellipsis ? "block min-w-0 max-w-full" : undefined}
      title={ellipsis && text ? text : undefined}
    >
      <div
        className={TWclassNames(
          className ? className : "text-blue-400",
          ellipsis && "truncate"
        )}
      >
        {text}
      </div>
    </Link>
  );
};

export const ReportTitle = ({
  report,
  path = reportPath({ report: report }),
  className,
}) => {
  return (
    <Link to={path}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {report?.displayTitle}
      </div>
    </Link>
  );
};

export const OrderTitle = ({
  order,
  path = orderPath({ order: order }),
  className,
}) => {
  return (
    <Link to={path}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {order?.displayTitle}
      </div>
    </Link>
  );
};

export const ContactTitle = ({ contact, path: _path = null, className }) => {
  return (
    <Link to={null}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {contact?.displayTitle}
      </div>
    </Link>
  );
};

export const CustomerTitle = ({
  customer,
  path = customerPath({ customer: customer }),
  className,
  useDialog = false,
  ellipsis = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const text = customer?.displayTitle;

  if (useDialog) {
    return (
      <>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className={TWclassNames(
            className ? className : "text-blue-400",
            ellipsis && "block max-w-full truncate text-left"
          )}
          title={ellipsis && text ? text : undefined}
        >
          {text}
        </button>
        <CustomerDialog
          open={dialogOpen}
          setOpen={setDialogOpen}
          onClose={() => setDialogOpen(false)}
          selected={customer}
        />
      </>
    );
  } else {
    return (
      <Link
        to={path}
        className={ellipsis ? "block min-w-0 max-w-full" : undefined}
        title={ellipsis && text ? text : undefined}
      >
        <div
          className={TWclassNames(
            className ? className : "text-blue-400",
            ellipsis && "truncate"
          )}
        >
          {text}
        </div>
      </Link>
    );
  }
};

export const UserTitle = ({
  user,
  path = userPath({ user: user }),
  className,
  useDialog: _useDialog = false,
}) => {
  return (
    <Link to={path}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {user?.displayTitle}
      </div>
    </Link>
  );
};

export const SystemTitle = ({ system, path: _path = null, className }) => {
  return (
    <Link to={null}>
      <div className={TWclassNames(className ? className : "text-blue-400")}>
        {system?.displayTitle}
      </div>
    </Link>
  );
};

export const DeliveryAddressTitle = ({
  deliveryAddress,
  path = deliveryAddressGooglePath({ deliveryAddress: deliveryAddress }),
  className,
  ellipsis = false,
}) => {
  if (!deliveryAddress?.id) return <span>Missing delivery address</span>;
  const text = deliveryAddress?.displayTitle;
  return (
    <Link
      to={path}
      className={ellipsis ? "block min-w-0 max-w-full" : undefined}
      title={ellipsis && text ? text : undefined}
    >
      <div
        className={TWclassNames(
          className ? className : "text-blue-400",
          ellipsis && "truncate"
        )}
      >
        {text}
      </div>
    </Link>
  );
};

export const NewLineFormatter = ({ value }) => {
  if (value == null || typeof value !== "string") return null;
  const isHTML = /<\/?[a-z][\s\S]*>/i.test(value); // Basic regex to check if the string contains HTML tags

  if (isHTML) {
    return (
      <div
        className="htmlContent"
        dangerouslySetInnerHTML={{
          __html: value,
        }}
      />
    );
  } else {
    const text = value?.split("\n").map((str, index) => (
      <Fragment key={index}>
        {str}
        <br />
      </Fragment>
    ));

    return <div>{text}</div>;
  }
};

// Create a counting animation that starts from 0 and stops at the value
// The animation is triggered when the component is mounted
export const CountUp = ({ value, duration = 1 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const framesPerSecond = 60;
    const totalFrames = framesPerSecond * duration;
    const increment = value / totalFrames;

    const interval = setInterval(() => {
      setCount((prevCount) => {
        if (prevCount < value) {
          return Math.min(prevCount + increment, value);
        } else {
          clearInterval(interval);
          return prevCount;
        }
      });
    }, (duration * 1000) / framesPerSecond);

    return () => clearInterval(interval);
  }, [duration, value]);

  return <span>{count.toFixed(2)}</span>;
};

export function generateRandomString() {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 5; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export const TimeSinceInWords = ({ date }) => {
  const now = new Date();
  const updated = new Date(date);
  const diff = now - updated;
  const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diff / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diff / (1000 * 60));
  const diffSeconds = Math.floor(diff / 1000);

  if (diffDays > 0) {
    return `${diffDays} dager siden`;
  }
  if (diffHours > 0) {
    return `${diffHours} timer siden`;
  }
  if (diffMinutes > 0) {
    return `${diffMinutes} minutter siden`;
  }
  if (diffSeconds > 0) {
    return `${diffSeconds} sekunder siden`;
  }
  return "Nå";
};

export const fullDeliveryAddress = (address) => {
  if (!address) return "";
  const parts = [
    address.address,
    address.address2,
    [address.postal, address.place].filter(Boolean).join(" "),
    address.country,
  ].filter((part) => String(part || "").trim().length > 0);

  return parts.join(", ");
};
export function OperationStatusWithNotice({
  operations,
  operationName,
  title,
}) {

  // filter operations by operation name
  const filteredOperations = operations?.filter(
    (operation) => operation?.message === operationName
  );

  function setCanBeCompleted() {
    // Check if any filteredOperations has can_be_completed set to false
    const canBeCompleted = filteredOperations?.every(
      (operation) => operation?.can_be_completed === true
    );

    // return false if any operation has can_be_completed set to false
    return canBeCompleted;
  }

  function description() {
    if (filteredOperations.length === 0) return "Alt er bra ut!";

    if (setCanBeCompleted()) return "Rapporten kan ferdigstilles uten";

    return "Rapporten kan ikke ferdigstilles uten";
  }

  function type() {
    if (filteredOperations.length === 0) return "success";

    if (setCanBeCompleted()) return "warning";

    return "error";
  }

  return <PMNotice title={title} description={description()} type={type()} />;
}

export function OperationsStatus({ operations, operationName, title: _title }) {
  const { t } = useTranslation();

  // filter operations by operation name
  const filteredOperations = operations?.filter(
    (operation) => operation?.name === operationName
  );

  // make sure that filteredOperations has unique messages
  const uniqueMessages = [
    ...new Set(filteredOperations.map((operation) => operation.message)),
  ];

  function setCanBeCompleted() {
    // Check if any filteredOperations has can_be_completed set to false
    const canBeCompleted = filteredOperations?.every(
      (operation) => operation?.can_be_completed === true
    );

    // return false if any operation has can_be_completed set to false
    return canBeCompleted;
  }

  if (setCanBeCompleted()) return null;

  function description() {
    if (filteredOperations.length === 0) return "Alt er bra ut!";

    if (setCanBeCompleted()) return "";

    return uniqueMessages
      .filter((m) => typeof m === "string" || typeof m === "number")
      .map((message, index) => (
        <li key={index}>{t(String(message))}</li>
      ));
  }

  function type() {
    if (filteredOperations.length === 0) return "success";

    if (setCanBeCompleted()) return "warning";

    return "error";
  }

  return (
    <PMNotice
      title={"Kan ikke utføres før dette er rettet"}
      description={<ul className="ml-4 list-disc">{description()}</ul>}
      type={type()}
    />
  );
}

export function OperationObjectStatus({ operations: _operations, objectName: _objectName }) {}

export function OperationCanFinish({ operations, operationName }) {
  // filter operations by operation name
  const filteredOperations = operations?.filter(
    (operation) => operation?.name === operationName
  );

  function canBeCompleted() {
    // Check if any filteredOperations has can_be_completed set to false
    const canBeCompleted = filteredOperations?.every(
      (operation) => operation?.can_be_completed === true
    );

    // return false if any operation has can_be_completed set to false
    return canBeCompleted;
  }

  if (filteredOperations?.length === 0) return true;

  if (canBeCompleted()) return true;

  return false;
}

function LoadingColumns({ colSpan }) {
  return (
    <td colSpan={colSpan} className="px-5 py-4 whitespace-nowrap">
      <div className="flex flex-col justify-center">
        <div className="flex flex-col animate-pulse">
          <div className="w-20 h-4 mb-2 bg-gray-400 rounded"></div>
          <div className="w-20 h-4 bg-gray-400 rounded"></div>
        </div>
      </div>
    </td>
  );
}

export function LoadingRows({ cols = 5, checkboxCell = false }) {
  const cells = [];

  for (let i = 0; i < cols; i++) {
    cells.push(<LoadingColumns colSpan="1" />);
  }

  return (
    <tr>
      {checkboxCell && <td className="px-5 py-4 whitespace-nowrap" />}
      {cells}
    </tr>
  );
}

export const searchFieldsByName = (item, name) => {
  return item?.fields?.find((field) => field?.name === name);
};

// Returns the change (array with 2 values, old and new) of a field in a form
export function ChangedValues({ change }) {
  const { t } = useTranslation();

  if (!change) return null;

  // change can be array [old, new] or object {from/to, old/new} - extract primitives only
  let rawOld, rawNew;
  if (Array.isArray(change)) {
    rawOld = change[0];
    rawNew = change[1];
  } else if (typeof change === "object") {
    rawOld = change.from ?? change.old ?? change[0];
    rawNew = change.to ?? change.new ?? change[1];
  } else {
    return null;
  }

  const oldValue =
    typeof rawOld === "string" || typeof rawOld === "number"
      ? String(rawOld)
      : null;
  const newValue =
    typeof rawNew === "string" || typeof rawNew === "number"
      ? String(rawNew)
      : null;

  if (oldValue === newValue) return null;

  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-red-500"></div>
      <div className="ml-1 text-sm font-medium text-gray-900">
        {t("Changed from")} {oldValue ? t(oldValue) : t("None")} {t("to")}{" "}
        {newValue ? t(newValue) : t("None")}
      </div>
    </div>
  );
}

// function that takes a name and returns a tag with initials
export function getInitials(name) {
  if (!name) return null;
  const initials = name.match(/\b\w/g) || [];

  // return first and last
  return initials.shift() + initials.pop();
}

//
export function InitialsTag({ name }) {
  if (!name) return null;

  // Improved hash function
  const hashString = (str) => {
    let hash = 5381; // Using a larger initial prime number to start
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash * 33) ^ char; // Multiply hash by 33 and XOR with the character code
    }
    return Math.abs(hash); // Ensure the hash is non-negative
  };

  const colors = [
    "bg-red-500",
    "bg-yellow-500",
    "bg-green-500",
    "bg-blue-500",
    "bg-indigo-500",
    "bg-pink-500",
    "bg-purple-500",
    "bg-gray-500",
    "bg-orange-500",
  ];

  const colorIndex = hashString(name) % colors.length;
  const color = colors[colorIndex];

  // Text color for contrast
  const textColor = "text-white";

  return (
    <span
      className={
        "inline-flex items-center justify-center h-6 w-6 rounded-full " + color
      }
    >
      <span
        className={TWclassNames(textColor, "text-sm font-medium leading-none")}
      >
        {getInitials(name)}
      </span>
    </span>
  );
}

export const PromptBeforeClose = ({ formik, message }) => {
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (formik.dirty) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [formik.dirty]);

  // useEffect(() => {
  //   window.onbeforeunload = isDirty ? () => true : undefined;
  // }, [isDirty]);

  return <SimplePrompt open={isDirty} message={message} />;
};

// Remove html from text, but format line-breaks as /n
export const removeHtml = (text) => {
  if (!text) return null;

  const regex = /(<([^>]+)>)/gi;

  const result = typeof text === "string" ? text.replace(regex, "\n") : null;

  return result;
};

export const GoogleMapsButton = ({ address }) => {
  const FULL_ADDRESS =
    address?.address +
    ", " +
    (address?.address2 ? ", " + address?.address2 : "") +
    address?.postal +
    ", " +
    address?.place;

  if (address?.address?.length > 2) {
    return (
      <div className="">
        <a
          aria-disabled={address ? true : false}
          href={"https://www.google.com/maps?q=" + FULL_ADDRESS}
          rel="noreferrer"
          target="_blank"
          type="button"
          className="inline-flex items-center rounded-md border border-transparent bg-blue-500 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <MapIcon className="h-4 w-4" aria-hidden="true" />
          <span className="ml-2">Google</span>
        </a>
      </div>
    );
  } else {
    return null;
  }
};

// Utility function to add classes dynamically
const addClassesToTags = (node, classes) => {
  if (node && node.type === "tag" && classes[node.name]) {
    node.attribs = { ...node.attribs, class: classes[node.name] };
  }
  return node;
};

export const HtmlTextFormatter = ({ string, classes }) => {
  // if no classes, use default classes
  if (!classes) {
    classes = {
      p: "text-base mb-4",
      h1: "text-2xl font-bold",
      h2: "text-xl font-bold",
      h3: "text-lg font-bold",
      h4: "text-base font-bold",
      h5: "text-sm font-bold",
      h6: "text-xs font-bold",
    };
  }

  if (!string) return null;

  // Example: string = "<p>Some text</p><h1>Title</h1>"
  // Example: classes = { p: 'text-base mb-4', h1: 'text-2xl font-bold' }

  // Parse and apply the classes
  const parsedHtml = parse(string, {
    replace: (domNode) => addClassesToTags(domNode, classes),
  });

  return <div>{parsedHtml}</div>;
};

export const SeoHelmetWrapper = ({ title, description, subtitle }) => {
  return (
    <Helmet prioritizeSeoTags>
      <title>{"Promang - " + title + (subtitle ? " - " + subtitle : "")}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="nofollow, noindex" />
      <meta name="author" content="Promang" />
      <meta name="publisher" content="Promang" />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={description} />
      <meta property="og:locale" content="nb_NO" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Promang" />
      {/* <meta property="og:image" content={fullImagePath} /> */}
      <meta property="og:image:alt" content="Promang" />
      {/* <meta name="twitter:card" content={fullImagePath} /> */}
      <meta name="twitter:site" content="@promang" />
      <meta name="twitter:creator" content="@promang" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <link rel="canonical" href="https://www.promang.no/" />
    </Helmet>
  );
};

export const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html || "";
  return tmp.textContent || tmp.innerText || "";
};

export const yupHtmlString = (message = "Tekstfelt må inneholde innhold") =>
  Yup.string().test("has-meaningful-text", message, (value) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = value || "";
    const text = tmp.textContent || tmp.innerText || "";
    return text.trim().length > 0;
  });

export const MODULE_URL = (type, id, extra) => {
  if (!type || !id) return "/";
  switch (type.toLowerCase()) {
    case "report":
      return `/dashboard/service/reports/${id}`;
    case "case":
      return `/dashboard/cases?caseId=${id}`;
    case "project":
      return `/dashboard/projects/${id}`;
    case "order":
      return `/dashboard/orders/${id}`;
    case "customer_communication": {
      const customerId =
        extra && typeof extra === "object"
          ? extra.customer_id || extra.customerId
          : extra;
      if (customerId) {
        return `/dashboard/customers/${customerId}/communications/${id}`;
      }
      return "/dashboard/customers";
    }
    default:
      return "/";
  }
};

export const CopyToClipboard = ({ value, className = "", size = "sm" }) => {
  const { t } = useTranslation();

  const handleCopy = async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("Copied to clipboard") || "Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error(t("Failed to copy") || "Failed to copy");
    }
  };

  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <button
      onClick={handleCopy}
      className={`text-gray-400 hover:text-gray-600 transition-colors duration-200 ${className}`}
      title={t("Copy to clipboard") || "Copy to clipboard"}
      type="button"
    >
      <ClipboardDocumentIcon className={sizeClasses[size]} aria-hidden="true" />
    </button>
  );
};
